/**
 * @fileoverview BudgetAllocationPage - Monthly allocation tracking page.
 *
 * Displays budget allocation status for any month, showing how much of
 * each category's budget has been allocated from template-based income and how
 * much remains available.
 *
 * @description This page component:
 * - Shows allocation summary (max, allocated, remaining per category)
 * - Supports viewing any month via month navigation
 * - Shows warnings when categories or template not configured
 * - Provides navigation to setup pages when needed
 *
 * Allocation display:
 * - Target: From budget template
 * - Allocated: From income transactions using template
 * - Remaining: Target - Allocated
 *
 * Component composition:
 * - BackButton: Return to main page
 * - MonthNavigation: Month selector
 * - MonthlyAllocations: Allocation display component
 * - Conditional warnings for missing setup
 *
 * @module pages/BudgetAllocationPage
 */
import { useState, useMemo } from 'react';
import { useBudgetTemplate } from '../hooks/useBudgetTemplate';
import { useMonthlyAllocations } from '../hooks/useMonthlyAllocations';
import MonthlyAllocations from '../components/BudgetTemplate/MonthlyAllocations';
import BackButton from '../components/shared/BackButton';
import MonthNavigation from '../components/MonthNavigation/MonthNavigation';
import styles from './BudgetAllocationPage.module.css';

/**
 * Helper function to check if a category is currently active.
 *
 * @param {Object} cat - Category object with endDate
 * @returns {boolean} Whether category is active
 */
const isActiveCategory = (cat) => {
  if (!cat.endDate) return true;
  const endDateStr = cat.endDate.split('T')[0];
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return endDateStr > todayStr;
};

/**
 * Budget allocation tracking page with month navigation.
 *
 * @param {Object} props - Component props
 * @param {Object} props.categoryHook - Category hook
 * @param {Function} props.onBack - Navigate back handler
 * @param {Function} props.onNavigateToCategories - Navigate to categories
 * @param {Function} props.onNavigateToBudgetTemplate - Navigate to template
 */
function BudgetAllocationPage({
  categoryHook,
  onBack,
  onNavigateToCategories,
  onNavigateToBudgetTemplate
}) {
  // Month selector state (format: "YYYY-MM")
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );

  // Get budget template for the selected month
  const budgetHook = useBudgetTemplate(selectedMonth);

  // Get monthly allocations for the selected month
  const { allocations: monthlyAllocations, isLoading: allocationsLoading } = useMonthlyAllocations(
    selectedMonth,
    budgetHook.budgetTemplate
  );

  // Compute allocation summary combining template targets with allocated amounts
  const allocationSummary = useMemo(() => {
    if (!budgetHook.budgetTemplate || !monthlyAllocations) {
      return null;
    }

    const summary = {};
    Object.entries(budgetHook.budgetTemplate).forEach(([categoryName, settings]) => {
      const categoryData = monthlyAllocations[categoryName] || { allocated: 0, remaining: settings.target };
      summary[categoryName] = {
        target: settings.target,
        allocated: categoryData.allocated,
        remaining: categoryData.remaining
      };
    });

    return summary;
  }, [budgetHook.budgetTemplate, monthlyAllocations]);

  // Month navigation handlers
  const handlePreviousMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    date.setMonth(date.getMonth() - 1);
    setSelectedMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleNextMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    date.setMonth(date.getMonth() + 1);
    setSelectedMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleGoToCurrentMonth = () => {
    const now = new Date();
    setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  };

  const isCurrentMonth = () => {
    const now = new Date();
    const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return selectedMonth === currentMonthStr;
  };

  const getSelectedMonthDisplay = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const hasValidCategories = categoryHook.categories.some(isActiveCategory);
  const hasTemplate = budgetHook.budgetTemplate && Object.keys(budgetHook.budgetTemplate).length > 0 && !budgetHook.noTemplate;

  // Show loading state
  if (budgetHook.isLoading || allocationsLoading) {
    return (
      <div className={styles.container}>
        <BackButton onClick={onBack} />
        <h1 className={styles.pageTitle}>Budget Allocation</h1>
        <MonthNavigation
          selectedMonthDisplay={getSelectedMonthDisplay()}
          isCurrentMonth={isCurrentMonth()}
          onPreviousMonth={handlePreviousMonth}
          onNextMonth={handleNextMonth}
          onGoToCurrentMonth={handleGoToCurrentMonth}
          hideBalances={true}
        />
        <p>Loading allocation data...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <BackButton onClick={onBack} />
      <h1 className={styles.pageTitle}>Budget Allocation</h1>

      <MonthNavigation
        selectedMonthDisplay={getSelectedMonthDisplay()}
        isCurrentMonth={isCurrentMonth()}
        onPreviousMonth={handlePreviousMonth}
        onNextMonth={handleNextMonth}
        onGoToCurrentMonth={handleGoToCurrentMonth}
        hideBalances={true}
      />

      {budgetHook.noTemplate && (
        <div className={styles.noTemplateMessage}>
          <p>No budget template configured for {getSelectedMonthDisplay()}.</p>
          <p className={styles.noTemplateHint}>
            Budget allocations are only available when a template is configured.
          </p>
        </div>
      )}

      {!budgetHook.noTemplate && !hasValidCategories && !hasTemplate && (
        <div className={styles.warningContainer}>
          <span className={styles.warningText}>
            Please add categories and then add a budget template
          </span>
          <button
            onClick={onNavigateToCategories}
            className={styles.warningButton}
          >
            Go to Categories
          </button>
        </div>
      )}

      {!budgetHook.noTemplate && hasValidCategories && !hasTemplate && (
        <div className={styles.warningContainer}>
          <span className={styles.warningText}>
            Please add a budget template
          </span>
          <button
            onClick={onNavigateToBudgetTemplate}
            className={styles.warningButton}
          >
            Go to Budget Template
          </button>
        </div>
      )}

      {!budgetHook.noTemplate && (
        <MonthlyAllocations
          monthlyAllocations={allocationSummary}
          budgetTemplate={budgetHook.budgetTemplate}
          isCurrentMonth={isCurrentMonth()}
          alwaysExpanded={true}
        />
      )}
    </div>
  );
}

export default BudgetAllocationPage;
